"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, PanInfo, useAnimation } from "framer-motion";
import ProductCard from "@/components/products/product-card";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Product {
  id: number;
  name: string;
  price: number;
  slug: string;
  stock: number;
  description: string;
  image_url: string;
  has_variants: boolean;
}

export default function LatestArrivals() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // For drag/swipe functionality
  const x = useMotionValue(0);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(12);

        if (error) throw error;

        if (data) {
          setProducts(data);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        // Fallback to sample data if Supabase isn't connected yet
        setProducts([
          {
            id: 13,
            name: "Macallan 12 Year Old",
            price: 35000,
            slug: "macallan-12-year-old",
            stock: 12,
            description: "A rich, sweet single malt with notes of dried fruits and spices.",
            image_url: "https://images.pexels.com/photos/3019019/pexels-photo-3019019.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 14,
            name: "Château Margaux 2015",
            price: 150000,
            slug: "chateau-margaux-2015",
            stock: 5,
            description: "An exceptional vintage from one of Bordeaux's premier estates.",
            image_url: "https://images.pexels.com/photos/2912108/pexels-photo-2912108.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false   
          },
          {
            id: 15,
            name: "Guinness Foreign Extra",
            price: 1500,
            slug: "guinness-foreign-extra",
            stock: 100,
            description: "A bold, flavorful stout with a rich heritage and distinctive taste.",
            image_url: "https://images.pexels.com/photos/1269043/pexels-photo-1269043.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 16,
            name: "Moët & Chandon Brut Imperial",
            price: 25000,
            slug: "moet-chandon-brut-imperial",
            stock: 20,
            description: "A harmonious champagne with bright fruitiness and elegant maturity.",
            image_url: "https://images.pexels.com/photos/2795438/pexels-photo-2795438.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 17,
            name: "Hibiki Japanese Harmony",
            price: 32000,
            slug: "hibiki-japanese-harmony",
            stock: 15,
            description: "A harmonious blend of Japanese malt and grain whiskies with a delicate, floral profile.",
            image_url: "https://images.pexels.com/photos/2701570/pexels-photo-2701570.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 18,
            name: "Aperol Spritz Kit",
            price: 8500,
            slug: "aperol-spritz-kit",
            stock: 30,
            description: "Everything you need to make the perfect Italian aperitif at home.",
            image_url: "https://images.pexels.com/photos/5947053/pexels-photo-5947053.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 19,
            name: "Absolut Elyx Vodka",
            price: 15000,
            slug: "absolut-elyx-vodka",
            stock: 25,
            description: "Luxury vodka crafted from single estate wheat, with a silky, refined texture.",
            image_url: "https://images.pexels.com/photos/271837/pexels-photo-271837.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 20,
            name: "Casamigos Reposado Tequila",
            price: 18000,
            slug: "casamigos-reposado-tequila",
            stock: 18,
            description: "Aged for 7 months in American white oak barrels for a smooth, clean finish.",
            image_url: "https://images.pexels.com/photos/4021983/pexels-photo-4021983.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false
          },
          {
            id: 21,
            name: "Diplomatico Reserva Exclusiva Rum",
            price: 14000,
            slug: "diplomatico-reserva-exclusiva-rum",
            stock: 22,
            description: "Award-winning Venezuelan rum with notes of maple syrup, orange peel, and licorice.",
            image_url: "https://images.pexels.com/photos/6363791/pexels-photo-6363791.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false 
          },
          {
            id: 22,
            name: "Lagavulin 16 Year Old",
            price: 28000,
            slug: "lagavulin-16-year-old",
            stock: 10,
            description: "Intensely flavored, smoky and rich, the ultimate Islay malt scotch whisky.",
            image_url: "https://images.pexels.com/photos/4021984/pexels-photo-4021984.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false 
          },
          {
            id: 23,
            name: "Dom Ruinart Blanc de Blancs",
            price: 65000,
            slug: "dom-ruinart-blanc-de-blancs",
            stock: 8,
            description: "Elegant champagne made exclusively from Chardonnay grapes with exceptional finesse.",
            image_url: "https://images.pexels.com/photos/6151702/pexels-photo-6151702.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false 
          },
          {
            id: 24,
            name: "Glenfiddich 21 Year Gran Reserva",
            price: 45000,
            slug: "glenfiddich-21-year-gran-reserva",
            stock: 7,
            description: "Aged in Caribbean rum casks, delivering a complex whisky with spicy warmth.",
            image_url: "https://images.pexels.com/photos/3731256/pexels-photo-3731256.jpeg?auto=compress&cs=tinysrgb&w=600",
            has_variants: false 
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Calculate how many products to show per slide based on screen size
  const getProductsPerSlide = () => {
    if (typeof window === 'undefined') return 4; // Default for SSR
    if (window.innerWidth < 640) return 2; // Mobile
    if (window.innerWidth < 768) return 2; // Small tablet
    if (window.innerWidth < 1024) return 3; // Large tablet
    return 4; // Desktop
  };
  
  const [productsPerSlide, setProductsPerSlide] = useState(getProductsPerSlide());
  const [totalSlides, setTotalSlides] = useState(Math.ceil(products.length / productsPerSlide));
  
  // Update container dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        const newProductsPerSlide = getProductsPerSlide();
        setProductsPerSlide(newProductsPerSlide);
        setTotalSlides(Math.ceil(products.length / newProductsPerSlide));
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [products.length]);
  
  // Update position when currentIndex changes or container width changes
  useEffect(() => {
    if (containerWidth > 0) {
      controls.start({
        x: -currentIndex * containerWidth,
        transition: { type: "spring", stiffness: 300, damping: 30 }
      });
    }
  }, [currentIndex, containerWidth, controls]);
  
  // Reset current index when total slides changes
  useEffect(() => {
    if (currentIndex >= totalSlides) {
      setCurrentIndex(totalSlides > 0 ? totalSlides - 1 : 0);
    }
  }, [totalSlides, currentIndex]);
  
  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    const threshold = containerWidth / 4; // 25% of container width
    
    if (Math.abs(info.offset.x) > threshold) {
      // If dragged far enough, switch slide
      if (info.offset.x > 0 && currentIndex > 0) {
        // Dragged right - go to previous slide
        prevSlide();
      } else if (info.offset.x < 0 && currentIndex < totalSlides - 1) {
        // Dragged left - go to next slide
        nextSlide();
      } else {
        // Bounce back if at the end
        controls.start({
          x: -currentIndex * containerWidth,
          transition: { type: "spring", stiffness: 300, damping: 30 }
        });
      }
    } else {
      // If not dragged far enough, snap back
      controls.start({
        x: -currentIndex * containerWidth,
        transition: { type: "spring", stiffness: 300, damping: 30 }
      });
    }
  };
  
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">Latest Arrivals</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={prevSlide}
            className="rounded-full hidden sm:flex"
            disabled={loading || products.length <= productsPerSlide}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={nextSlide}
            className="rounded-full hidden sm:flex"
            disabled={loading || products.length <= productsPerSlide}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button variant="outline" asChild>
            <a href="/new-arrivals">View All</a>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="h-80 bg-muted rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div 
          ref={containerRef}
          className="overflow-hidden relative cursor-grab"
        >
          <motion.div
            className="w-full" 
            animate={controls}
            drag="x"
            dragConstraints={{ left: -containerWidth * (totalSlides - 1), right: 0 }}
            dragElastic={0.1}
            dragMomentum={true}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            style={{ touchAction: 'pan-y' }}
          >
            <div className="flex">
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div 
                  key={slideIndex} 
                  className="min-w-full w-full"
                  style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 px-1">
                    {products
                      .slice(
                        slideIndex * productsPerSlide, 
                        (slideIndex * productsPerSlide) + productsPerSlide
                      )
                      .map(product => (
                        <div key={product.id} className="h-full">
                          <ProductCard product={product} />
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Mobile swipe indicator */}
          <div className="mt-4 sm:hidden text-center text-sm text-muted-foreground">
            <p>Swipe to see more</p>
          </div>
        </div>
      )}
      
      {/* Slide indicators */}
      {!loading && products.length > productsPerSlide && (
        <div className="flex justify-center mt-4 gap-1">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full ${index === currentIndex ? 'bg-primary' : 'bg-muted'}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      )}
    </section>
  );
}